import type { TFunction } from 'i18next';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { isValidEmailAddress } from './invoicePayerEmailsSchema';
import * as Ch from './styled';

export const MAX_BILLING_EMAILS = 5;

export type InvoicePayerEmailsFlushResult = {
  emails: string[];
  error: string | null;
};

export type InvoicePayerEmailsFieldHandle = {
  /** Commits the in-progress draft and returns the resulting emails / field error. */
  flush: () => InvoicePayerEmailsFlushResult;
};

type InvoicePayerEmailsFieldProps = {
  emails: string[];
  onChange: (emails: string[]) => void;
  inputId: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  placeholder?: string;
  optional?: boolean;
  maxEmails?: number;
  children?: React.ReactNode;
};

function applyEmailDraft(
  raw: string,
  current: string[],
  maxEmails: number,
  t: TFunction,
): InvoicePayerEmailsFlushResult & { draft: string } {
  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) {
    return { emails: current, draft: '', error: null };
  }
  let accepted = current;
  let rejected = '';
  let error: string | null = null;
  for (const part of parts) {
    if (accepted.length >= maxEmails) {
      error = t('subscriptions.checkout.chf.maxRecipients');
      rejected = part;
      break;
    }
    if (!isValidEmailAddress(part)) {
      error = t('subscriptions.checkout.chf.invalidEmail');
      rejected = part;
      break;
    }
    if (!accepted.includes(part)) accepted = [...accepted, part];
  }
  return { emails: accepted, draft: rejected, error };
}

const InvoicePayerEmailsField = forwardRef<
  InvoicePayerEmailsFieldHandle,
  InvoicePayerEmailsFieldProps
>(function InvoicePayerEmailsField(
  {
    emails,
    onChange,
    inputId,
    label,
    hint,
    placeholder,
    optional = false,
    maxEmails = MAX_BILLING_EMAILS,
    children,
  },
  ref,
) {
  const { t } = useTranslation();
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailFocused, setEmailFocused] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const emailsRef = useRef(emails);
  emailsRef.current = emails;

  const commitEmailDraft = useCallback(
    (raw: string) => {
      const result = applyEmailDraft(raw, emailsRef.current, maxEmails, t);
      if (result.emails !== emailsRef.current) onChange(result.emails);
      setEmailDraft(result.draft);
      setEmailError(result.error);
      return result;
    },
    [maxEmails, onChange, t],
  );

  useImperativeHandle(
    ref,
    () => ({
      flush: () => {
        const result = applyEmailDraft(
          emailDraft,
          emailsRef.current,
          maxEmails,
          t,
        );
        if (result.emails !== emailsRef.current) onChange(result.emails);
        setEmailDraft(result.draft);
        setEmailError(result.error);
        return { emails: result.emails, error: result.error };
      },
    }),
    [emailDraft, maxEmails, onChange, t],
  );

  const removeEmail = useCallback(
    (value: string) => {
      onChange(emailsRef.current.filter((email) => email !== value));
      setEmailError(null);
    },
    [onChange],
  );

  const onEmailKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        commitEmailDraft(emailDraft);
        return;
      }
      if (e.key === 'Backspace' && !emailDraft && emails.length) {
        e.preventDefault();
        removeEmail(emails[emails.length - 1]);
      }
    },
    [commitEmailDraft, emailDraft, emails, removeEmail],
  );

  const atRecipientLimit = emails.length >= maxEmails;
  const hintId = `${inputId}-hint`;

  return (
    <Ch.InvoiceFieldGroup>
      <Ch.InvoiceFieldLabel htmlFor={inputId}>
        {label}
        {optional ? (
          <Ch.InvoiceOptionalTag>
            {t('subscriptions.checkout.chf.optional')}
          </Ch.InvoiceOptionalTag>
        ) : null}
      </Ch.InvoiceFieldLabel>
      <Ch.InvoiceChipField
        $focused={emailFocused}
        onClick={() => emailInputRef.current?.focus()}
      >
        <Ch.InvoiceEmailIcon aria-hidden="true" />
        {emails.map((email) => (
          <Ch.InvoiceChip key={email}>
            <Ch.InvoiceChipText>{email}</Ch.InvoiceChipText>
            <Ch.InvoiceChipRemove
              type="button"
              onClick={() => removeEmail(email)}
              aria-label={t('subscriptions.checkout.invoiceRemoveEmail')}
            >
              ×
            </Ch.InvoiceChipRemove>
          </Ch.InvoiceChip>
        ))}
        <Ch.InvoiceChipInput
          ref={emailInputRef}
          id={inputId}
          type="email"
          inputMode="email"
          autoComplete="email"
          value={emailDraft}
          placeholder={atRecipientLimit ? '' : placeholder}
          aria-invalid={Boolean(emailError)}
          aria-describedby={hint ? hintId : undefined}
          onFocus={() => setEmailFocused(true)}
          onBlur={() => {
            setEmailFocused(false);
            commitEmailDraft(emailDraft);
          }}
          onKeyDown={onEmailKeyDown}
          onChange={(e) => {
            const { value } = e.target;
            if (value.includes(',')) {
              commitEmailDraft(value);
              return;
            }
            setEmailDraft(value);
            setEmailError(null);
          }}
        />
      </Ch.InvoiceChipField>
      {emailError ? (
        <Ch.InvoiceFieldError role="alert">{emailError}</Ch.InvoiceFieldError>
      ) : null}
      {hint ? <Ch.InvoiceHint id={hintId}>{hint}</Ch.InvoiceHint> : null}
      {children}
    </Ch.InvoiceFieldGroup>
  );
});

export default InvoicePayerEmailsField;
